/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { 
  collection, 
  query, 
  orderBy, 
  onSnapshot, 
  doc, 
  writeBatch,
  limit,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
  getDocs,
  where,
  updateDoc,
  deleteDoc,
  getDoc,
  setDoc,
  increment
} from 'firebase/firestore';
import { db } from '../../../shared/firebase';
import { updateProfile } from 'firebase/auth';
import { handleFirestoreError, OperationType } from '../../../shared/utils/firestoreError';
import { LeaguePlayer, GameRecord, View } from '../../../shared/types';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { GAME_SPECIFIERS, GameSpecifier } from '../../../shared/utils/gameSpecifiers';

interface LeagueContextType {
  currentLeagueId: string | null;
  setCurrentLeagueId: (id: string | null) => void;
  users: LeaguePlayer[];
  games: GameRecord[];
  view: View;
  setView: (view: View) => void;
  error: string | null;
  setError: (err: string | null) => void;
  successMessage: string | null;
  setSuccessMessage: (msg: string | null) => void;
  gameSpecifier: GameSpecifier;
  
  // Game Recording
  selectedPlayers: string[];
  setSelectedPlayers: React.Dispatch<React.SetStateAction<string[]>>;
  playerPoints: Record<string, number>;
  setPlayerPoints: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  roundOrders: string[][];
  setRoundOrders: React.Dispatch<React.SetStateAction<string[][]>>;
  activeRound: number;
  setActiveRound: React.Dispatch<React.SetStateAction<number>>;
  isRecording: boolean;
  handleRecordGame: () => Promise<void>;
  handleNextRound: () => void;
  
  // Pending Games
  pendingGames: GameRecord[];
  handleApprovePendingGame: (game: GameRecord) => Promise<void>;
  handleRejectPendingGame: (gameId: string) => Promise<void>;
  
  // Takeover logic
  claimingProfileId: string | null;
  setClaimingProfileId: (id: string | null) => void;
  handleRequestTakeover: (proxyId: string) => Promise<void>;
  handleCancelTakeover: (proxyId: string) => Promise<void>;
  handleApproveTakeover: (proxy: LeaguePlayer, requesterUid: string) => Promise<void>;
  handleDismissTakeover: (proxy: LeaguePlayer, requesterUid: string) => Promise<void>;
  
  // Helpers
  checkUsernameUnique: (name: string) => Promise<boolean>;
  isAdmin: boolean;
  isLeagueMod: boolean;
  isLeagueAdmin: boolean;
  myProfile: LeaguePlayer | undefined;
  
  // Management
  handleApproveJoinRequest: (uid: string) => Promise<void>;
  handleDenyJoinRequest: (uid: string) => Promise<void>;
  toggleModerator: (targetUser: LeaguePlayer) => Promise<void>;
  handleAddProxyProfile: (name: string, email?: string) => Promise<void>;
  handleDeleteUser: (uid: string, displayName: string) => Promise<void>;
  handleUpdateProfile: (editName: string, editPhoto: string) => Promise<void>;
  handleUpdateLeagueName: (newName: string) => Promise<void>;
  handleDeleteGame: (game: GameRecord) => Promise<void>;
  handleDeleteLeague: () => Promise<void>;
  handleJoinLeague: () => Promise<void>;
  handleRepairLeagueStats: () => Promise<void>;
  joinStatus: 'loading' | 'none' | 'pending' | 'member';
  isPlayersLoading: boolean;
  recordHighlight: boolean;
  setRecordHighlight: (val: boolean) => void;
  leagueData: any;
  myStats: {
    wins: number;
    winRate: string;
    highscore: number;
    averagePoints: string;
    recentGames: any[];
    recentPerformances: any[];
    potentialClaims: any[];
    myPendingClaim: any;
    enrichedMembers: any[];
    graphData: {
      weeks: any[];
      months: any[];
      years: any[];
      all: any[];
    };
    takeoverRequests: any[];
    arschlochStats: {
      comebacks: number;
      downfalls: number;
      avgRoundRank: string;
      arschlochQuote: string;
    } | null;
  };
}

const LeagueContext = createContext<LeagueContextType | undefined>(undefined);

export function LeagueProvider({ children }: { children: ReactNode }) {
  const { user, globalProfile } = useAuth();
  const [currentLeagueId, setCurrentLeagueId] = useState<string | null>(null);
  const [view, setView] = useState<View>('leaderboard');
  const [users, setUsers] = useState<LeaguePlayer[]>([]);
  const [isPlayersLoading, setIsPlayersLoading] = useState(true);
  const [games, setGames] = useState<GameRecord[]>([]);
  const [pendingGames, setPendingGames] = useState<GameRecord[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Auto-clear notifications
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const [claimingProfileId, setClaimingProfileId] = useState<string | null>(null);
  const [leagueData, setLeagueData] = useState<any>(null);
  const [recordHighlight, setRecordHighlight] = useState(false);

  const gameSpecifier = React.useMemo(() => {
    if (leagueData?.gameSpecifierId && GAME_SPECIFIERS[leagueData.gameSpecifierId]) {
      return GAME_SPECIFIERS[leagueData.gameSpecifierId];
    }
    if (leagueData?.rankingType === 'positional_elo_with_points') {
      return GAME_SPECIFIERS['general_points'];
    }
    return GAME_SPECIFIERS['general'];
  }, [leagueData]);

  // Record Game State
  const [selectedPlayers, setSelectedPlayers] = useState<string[]>([]);
  const [playerPoints, setPlayerPoints] = useState<Record<string, number>>({});
  const [roundOrders, setRoundOrders] = useState<string[][]>([]);
  const [activeRound, setActiveRound] = useState(0);
  const [isRecording, setIsRecording] = useState(false);

  useEffect(() => {
    if (currentLeagueId) {
      setView('leaderboard');
      setSelectedPlayers([]);
      setPlayerPoints({});
      setRoundOrders([]);
    }
  }, [currentLeagueId]);

  const myProfile = React.useMemo(() => users.find(u => u.uid === user?.uid), [users, user]);
  const isAdmin = globalProfile?.role === 'admin';
  const isLeagueAdmin = React.useMemo(() => myProfile?.leagueRole === 'admin' || myProfile?.globalRole === 'admin', [myProfile]);
  const isLeagueMod = React.useMemo(() => isAdmin || isLeagueAdmin || myProfile?.leagueRole === 'moderator' || myProfile?.globalRole === 'sitemod', [isAdmin, isLeagueAdmin, myProfile]);

  useEffect(() => {
    if (!currentLeagueId) {
      setUsers([]);
      setGames([]);
      setLeagueData(null);
      setIsPlayersLoading(false);
      return;
    }

    setIsPlayersLoading(true);
    const unsubLeague = onSnapshot(doc(db, 'leagues', currentLeagueId), (snap) => {
      setLeagueData(snap.data());
    });

    const usersQuery = query(collection(db, `leagues/${currentLeagueId}/players`), orderBy('elo', 'desc'));
    const gamesQuery = query(collection(db, `leagues/${currentLeagueId}/games`), orderBy('timestamp', 'desc'), limit(50));

    const unsubUsers = onSnapshot(usersQuery, { includeMetadataChanges: true }, (snap) => {
      const plys = snap.docs.map(d => d.data() as LeaguePlayer);
      setUsers(plys);
      
      // Don't dismiss loading screen if this is just a preliminary cache hit
      // Wait for the actual server response unless we are offline
      if (!snap.metadata.fromCache || !navigator.onLine) {
        setIsPlayersLoading(false);
      }
    }, (err) => {
      console.warn("League players fetch restricted (logged out or no access)");
      setIsPlayersLoading(false);
    });

    const unsubGames = onSnapshot(gamesQuery, (snap) => {
      setGames(snap.docs.map(d => ({ id: d.id, ...d.data() } as GameRecord)));
    }, (err) => {
      console.warn("League games fetch restricted (logged out or no access)");
    });

    let unsubPending: () => void = () => {};
    if (isLeagueMod) {
      const pendingQuery = query(collection(db, `leagues/${currentLeagueId}/pending_games`), orderBy('timestamp', 'desc'));
      unsubPending = onSnapshot(pendingQuery, (snap) => {
        setPendingGames(snap.docs.map(d => ({ id: d.id, ...d.data() } as GameRecord)));
      }, (err) => {
        console.warn("Pending games fetch restricted", err);
      });
    }

    return () => {
      unsubLeague();
      unsubUsers();
      unsubGames();
      unsubPending();
    };
  }, [currentLeagueId, isLeagueMod]);

  const joinStatus = React.useMemo<"none" | "loading" | "pending" | "member">(() => {
    if (!leagueData) return 'loading';
    return myProfile ? 'member' : (leagueData.pendingJoinRequests?.includes(user?.uid) ? 'pending' : 'none');
  }, [myProfile, leagueData, user]);

  const myStats = React.useMemo(() => {
    const myGames = games
      .filter(g => g.players.some(p => p.uid === user?.uid || (myProfile?.mergedUids || []).includes(p.uid)))
      .sort((a, b) => (a.timestamp?.toMillis() || 0) - (b.timestamp?.toMillis() || 0));

    const wins = myGames.filter(g => g.players.find(p => p.uid === user?.uid || (myProfile?.mergedUids || []).includes(p.uid))?.position === 1).length;
    const pointsList = myGames
      .map(g => g.players.find(p => p.uid === user?.uid || (myProfile?.mergedUids || []).includes(p.uid))?.points)
      .filter((p): p is number => p !== undefined);

    const recentPerformances = myGames.slice(-5).reverse().map(g => {
      const me = g.players.find(p => p.uid === user?.uid || (myProfile?.mergedUids || []).includes(p.uid))!;
      const allPlayersSorted = [...g.players]
        .sort((a, b) => a.position - b.position)
        .map(p => ({
          ...p,
          isMe: p.uid === user?.uid || (myProfile?.mergedUids || []).includes(p.uid),
          diff: p.eloAfter - p.eloBefore
        }));

      return {
        ...g,
        me,
        eloDiff: me.eloAfter - me.eloBefore,
        allPlayersSorted
      };
    });

    const isNewUser = !myProfile || myProfile.gamesPlayed === 0;
    const myPendingClaim = users.find(u => u.isAccountless && Array.isArray(u.pendingTakeoverBy) && u.pendingTakeoverBy.includes(user?.uid || ''));
    
    const potentialClaims = users.filter(u => 
      u.isAccountless && 
      user && !user.isAnonymous &&
      joinStatus === 'member' &&
      (!Array.isArray(u.pendingTakeoverBy) || !u.pendingTakeoverBy.includes(user.uid)) && 
      isNewUser &&
      (u.email === user.email?.toLowerCase() || !u.email)
    );

    const enrichedMembers = users.map(u => ({
      ...u,
      isMe: u.uid === user?.uid,
      canBeModerated: !u.isAccountless && u.leagueRole !== 'admin' && u.globalRole !== 'admin' && u.uid !== user?.uid,
      isMod: u.leagueRole === 'moderator' || u.globalRole === 'sitemod' || u.leagueRole === 'admin' || u.globalRole === 'admin'
    }));

    const calculateGraphPoints = (timeframe: 'weeks' | 'months' | 'years' | 'all') => {
      const now = Date.now();
      let filteredGames = myGames;
      
      if (timeframe === 'weeks') {
        filteredGames = myGames.filter(g => now - (g.timestamp?.toMillis() || 0) < 7 * 24 * 60 * 60 * 1000);
      } else if (timeframe === 'months') {
        filteredGames = myGames.filter(g => now - (g.timestamp?.toMillis() || 0) < 30 * 24 * 60 * 60 * 1000);
      } else if (timeframe === 'years') {
        filteredGames = myGames.filter(g => now - (g.timestamp?.toMillis() || 0) < 365 * 24 * 60 * 60 * 1000);
      }

      if (filteredGames.length === 0) return [];

      const initialValue = 1200;
      const dataPoints: { x: number, y: number, date: Date }[] = [];
      
      const firstFilteredTimestamp = filteredGames[0]?.timestamp?.toMillis() || 0;
      const gamesBefore = myGames.filter(g => (g.timestamp?.toMillis() || 0) < firstFilteredTimestamp);
      
      let currentVal = gamesBefore.length > 0 
        ? gamesBefore[gamesBefore.length - 1].players.find(p => p.uid === user?.uid || (myProfile?.mergedUids || []).includes(p.uid))!.eloAfter 
        : initialValue;

      filteredGames.forEach((g, i) => {
        const p = g.players.find(p => p.uid === user?.uid || (myProfile?.mergedUids || []).includes(p.uid))!;
        currentVal = p.eloAfter;
        dataPoints.push({ x: i, y: currentVal, date: g.timestamp?.toDate() || new Date() });
      });

      return dataPoints;
    };

    const graphData = {
      weeks: calculateGraphPoints('weeks'),
      months: calculateGraphPoints('months'),
      years: calculateGraphPoints('years'),
      all: calculateGraphPoints('all'),
    };

    const takeoverRequests = enrichedMembers.filter(u => 
      u.isAccountless && 
      (isAdmin || u.createdBy === user?.uid) && 
      Array.isArray(u.pendingTakeoverBy) && 
      u.pendingTakeoverBy.length > 0
    );

    let arschlochStats = null;
    if (gameSpecifier.id === 'arschloch') {
      let comebacks = 0;
      let downfalls = 0;
      let totalRounds = 0;
      let lastPlaces = 0;
      let sumRanks = 0;

      myGames.forEach(g => {
        const p = g.players.find(pl => pl.uid === user?.uid || (myProfile?.mergedUids || []).includes(pl.uid));
        if (p && p.positions) {
          totalRounds += p.positions.length;
          p.positions.forEach((pos, idx) => {
            sumRanks += pos;
            if (pos === g.players.length) lastPlaces++;
            if (idx > 0) {
              const prevPos = p.positions![idx - 1];
              if (prevPos === g.players.length && pos === 1) comebacks++;
              if (prevPos === 1 && pos === g.players.length) downfalls++;
            }
          });
        }
      });

      arschlochStats = {
        comebacks,
        downfalls,
        avgRoundRank: totalRounds > 0 ? (sumRanks / totalRounds).toFixed(2) : '0.0',
        arschlochQuote: totalRounds > 0 ? ((lastPlaces / totalRounds) * 100).toFixed(1) : '0.0'
      };
    }

    const enrichedMembersWithArschloch = enrichedMembers.map(u => {
      if (gameSpecifier.id !== 'arschloch') return u;
      
      const myGamesForUser = games.filter(g => g.players.some(p => p.uid === u.uid || (u.mergedUids || []).includes(p.uid)));
      let comebacks = 0;
      let downfalls = 0;
      let totalRounds = 0;
      let lastPlaces = 0;
      let sumRanks = 0;

      myGamesForUser.forEach(g => {
        const p = g.players.find(pl => pl.uid === u.uid || (u.mergedUids || []).includes(pl.uid));
        if (p && p.positions) {
          totalRounds += p.positions.length;
          p.positions.forEach((pos, idx) => {
            sumRanks += pos;
            if (pos === g.players.length) lastPlaces++;
            if (idx > 0) {
              const prevPos = p.positions![idx - 1];
              if (prevPos === g.players.length && pos === 1) comebacks++;
              if (prevPos === 1 && pos === g.players.length) downfalls++;
            }
          });
        }
      });

      return {
        ...u,
        arschloch: {
          comebacks,
          downfalls,
          avgRoundRank: totalRounds > 0 ? (sumRanks / totalRounds) : 0,
          arschlochQuote: totalRounds > 0 ? (lastPlaces / totalRounds) * 100 : 0,
          totalRounds
        }
      };
    });

    return {
      wins,
      winRate: myGames.length > 0 ? ((wins / myGames.length) * 100).toFixed(1) : '0.0',
      highscore: pointsList.length > 0 ? Math.max(...pointsList) : 0,
      averagePoints: pointsList.length > 0 ? (pointsList.reduce((a, b) => a + b, 0) / pointsList.length).toFixed(1) : '0.0',
      recentGames: myGames,
      recentPerformances,
      potentialClaims,
      myPendingClaim,
      enrichedMembers: enrichedMembersWithArschloch,
      graphData,
      takeoverRequests,
      arschlochStats
    };
  }, [games, user, myProfile, users, joinStatus, isAdmin, gameSpecifier]);

  const handleRepairLeagueStats = async () => {
    if (!currentLeagueId || !isLeagueMod) return;
    try {
      setSuccessMessage("Repairing stats... this may take a moment.");
      const gamesSnap = await getDocs(collection(db, `leagues/${currentLeagueId}/games`));
      const allGames = gamesSnap.docs.map(d => ({ id: d.id, ...d.data() } as GameRecord));
      
      const batch = writeBatch(db);
      batch.update(doc(db, 'leagues', currentLeagueId), { totalGames: allGames.length });
      users.forEach(u => {
        const myGames = allGames.filter(g => g.players.some(p => p.uid === u.uid || (u.mergedUids || []).includes(p.uid)));
        const totalPoints = myGames.reduce((sum, g) => {
          const p = g.players.find(pl => pl.uid === u.uid || (u.mergedUids || []).includes(pl.uid));
          return sum + (p?.points || 0);
        }, 0);
        const gamesPlayed = myGames.length;
        
        const playerRef = doc(db, `leagues/${currentLeagueId}/players`, u.uid);
        const updateData: any = { totalPoints, gamesPlayed };
        
        const sortedGames = [...myGames].sort((a, b) => (a.timestamp?.toMillis() || 0) - (b.timestamp?.toMillis() || 0));
        const latestGame = sortedGames[sortedGames.length - 1];
        if (latestGame) {
           const p = latestGame.players.find(pl => pl.uid === u.uid || (u.mergedUids || []).includes(pl.uid));
           if (p && p.eloAfter !== undefined) {
             updateData.elo = p.eloAfter;
           }
           if (latestGame.timestamp) updateData.lastGameAt = latestGame.timestamp;
        }
        
        batch.update(playerRef, updateData);
      });
      
      await batch.commit();
      setSuccessMessage("Stats repaired successfully!");
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setError("Failed to repair stats.");
      handleFirestoreError(err, OperationType.UPDATE, `leagues/${currentLeagueId}/players`);
    }
  };

  const handleJoinLeague = async () => {
    if (!user || user.isAnonymous || !currentLeagueId) return;
    try {
      await updateDoc(doc(db, 'leagues', currentLeagueId), {
        pendingJoinRequests: arrayUnion(user.uid)
      });
      setSuccessMessage("Join request sent!");
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setError("Failed to send join request.");
      handleFirestoreError(err, OperationType.UPDATE, `leagues/${currentLeagueId}`);
    }
  };

  const checkUsernameUnique = useCallback(async (name: string) => {
    if (!name || name.length < 2 || !currentLeagueId) return true;
    if (myProfile && name.toLowerCase() === myProfile.displayName.toLowerCase()) return true;
    
    // Optimize: Check local users state instead of querying Firestore to minimize reads
    const nameLower = name.toLowerCase();
    const isTaken = users.some(u => 
      (u.displayNameLower || u.displayName.toLowerCase()) === nameLower
    );
    return !isTaken;
  }, [currentLeagueId, myProfile, users]);

  const handleRequestTakeover = async (proxyId: string) => {
    if (!user || user.isAnonymous || !currentLeagueId) return;
    const alreadyClaiming = users.find(u => u.isAccountless && Array.isArray(u.pendingTakeoverBy) && u.pendingTakeoverBy.includes(user.uid));
    if (alreadyClaiming) {
      setError("You already have a pending claim request.");
      return;
    }
    try {
      const proxyProfile = users.find(u => u.uid === proxyId);
      const creator = proxyProfile ? users.find(u => u.uid === proxyProfile.createdBy) : null;
      const batch = writeBatch(db);
      const proxyRef = doc(db, `leagues/${currentLeagueId}/players`, proxyId);
      batch.update(proxyRef, { pendingTakeoverBy: arrayUnion(user.uid) });
      const myRef = doc(db, `leagues/${currentLeagueId}/players`, user.uid);
      if (!myProfile) {
         batch.set(myRef, {
            uid: user.uid,
            displayName: globalProfile?.displayName || user.displayName || 'Anonymous',
            displayNameLower: (globalProfile?.displayNameLower || user.displayName || 'Anonymous').toLowerCase(),
            photoURL: globalProfile?.photoURL || user.photoURL || '',
            elo: 1200,
            gamesPlayed: 0,
            claimingProfileId: proxyId
         });
      } else {
         batch.update(myRef, { claimingProfileId: proxyId });
      }
      await batch.commit();
      const message = creator 
        ? `Request sent! ${creator.displayName} will see your request in their profile settings.`
        : "Request sent! The creator will see your request in their profile settings.";
      setSuccessMessage(message);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setError("Failed to request takeover.");
      handleFirestoreError(err, OperationType.UPDATE, `leagues/${currentLeagueId}/players/${proxyId}`);
    }
  };

  const handleCancelTakeover = async (proxyId: string) => {
    if (!user || !currentLeagueId) return;
    try {
      const batch = writeBatch(db);
      const proxyRef = doc(db, `leagues/${currentLeagueId}/players`, proxyId);
      const myRef = doc(db, `leagues/${currentLeagueId}/players`, user.uid);
      batch.update(proxyRef, { pendingTakeoverBy: arrayRemove(user.uid) });
      batch.update(myRef, { claimingProfileId: null });
      await batch.commit();
      setSuccessMessage("Takeover request cancelled.");
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setError("Failed to cancel request.");
      handleFirestoreError(err, OperationType.UPDATE, `leagues/${currentLeagueId}/players/${proxyId}`);
    }
  };

  const handleApproveTakeover = async (proxy: LeaguePlayer, requesterUid: string) => {
    if (!user || !currentLeagueId) return;
    try {
      const batch = writeBatch(db);
      const realUserRef = doc(db, `leagues/${currentLeagueId}/players`, requesterUid);
      const proxyRef = doc(db, `leagues/${currentLeagueId}/players`, proxy.uid);
      const updateData: any = {
        elo: proxy.elo,
        totalPoints: increment(proxy.totalPoints || 0),
        gamesPlayed: increment(proxy.gamesPlayed || 0),
        claimingProfileId: null,
        leagueRole: proxy.leagueRole || 'member',
        mergedUids: arrayUnion(proxy.uid)
      };
      if (proxy.lastGameAt) updateData.lastGameAt = proxy.lastGameAt;
      batch.set(realUserRef, updateData, { merge: true });
      batch.delete(proxyRef);
      await batch.commit();
      setSuccessMessage(`Takeover approved! Stats have been merged.`);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setError("Failed to approve takeover.");
      handleFirestoreError(err, OperationType.UPDATE, `leagues/${currentLeagueId}/players/${proxy.uid}`);
    }
  };

  const handleDismissTakeover = async (proxy: LeaguePlayer, requesterUid: string) => {
    if (!user || !currentLeagueId) return;
    try {
      const batch = writeBatch(db);
      const proxyRef = doc(db, `leagues/${currentLeagueId}/players`, proxy.uid);
      const requesterRef = doc(db, `leagues/${currentLeagueId}/players`, requesterUid);
      batch.update(proxyRef, { pendingTakeoverBy: arrayRemove(requesterUid) });
      batch.update(requesterRef, { claimingProfileId: null });
      await batch.commit();
      setSuccessMessage("Takeover request dismissed.");
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setError("Failed to dismiss takeover request.");
      handleFirestoreError(err, OperationType.UPDATE, `leagues/${currentLeagueId}/players/${proxy.uid}`);
    }
  };

  const handleNextRound = useCallback(() => {
    const roundsCount = gameSpecifier?.roundsCount || 1;
    const nextRound = activeRound + 1;
    if (nextRound < roundsCount) {
      setRoundOrders(prev => {
        const next = [...prev];
        const currentRoundOrder = prev[activeRound];
        if (currentRoundOrder) {
          next[nextRound] = [...currentRoundOrder];
        } else {
          next[nextRound] = [...selectedPlayers];
        }
        return next;
      });
      setActiveRound(nextRound);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [activeRound, gameSpecifier, selectedPlayers]);

  const handleRecordGame = async () => {
    if (!currentLeagueId || !user) return;
    if (selectedPlayers.length < 2) {
      setError("Please select at least 2 players");
      return;
    }
    setIsRecording(true);
    setError(null);
    try {
      const isPointsMode = gameSpecifier.trackPoints;
      let playerDetails: any[] = [];
      if (isPointsMode) {
        const sortedByPoints = [...selectedPlayers].sort((a, b) => (playerPoints[b] || 0) - (playerPoints[a] || 0));
        let currentPos = 0;
        let lastPoints = -Infinity;
        playerDetails = sortedByPoints.map((uid, index) => {
          const u = users.find(u => u.uid === uid)!;
          const points = playerPoints[uid] || 0;
          if (index === 0 || points !== lastPoints) currentPos = index + 1;
          lastPoints = points;
          return { uid: u.uid, displayName: u.displayName, score: u.elo, position: currentPos, points };
        });
      } else if (gameSpecifier.hasRounds) {
        playerDetails = selectedPlayers.map((uid) => {
          const u = users.find(u => u.uid === uid)!;
          const positions = roundOrders.map(round => {
            const pos = round.indexOf(uid);
            return pos >= 0 ? pos + 1 : 1;
          });
          return { uid: u.uid, displayName: u.displayName, score: u.elo, position: positions[positions.length - 1], positions };
        });
      } else {
        playerDetails = selectedPlayers.map((uid, index) => {
          const u = users.find(u => u.uid === uid)!;
          return { uid: u.uid, displayName: u.displayName, score: u.elo, position: index + 1 };
        });
      }
      const eloResults = gameSpecifier.calculateEloChanges(playerDetails);
      const results = eloResults.map(r => {
        const p = playerDetails.find(pd => pd.uid === r.uid)!;
        return { uid: r.uid, displayName: p.displayName, position: p.position, positions: p.positions, points: p.points, eloBefore: p.score, eloAfter: r.newScore, newElo: r.newScore, roundEloChanges: r.roundEloChanges };
      });
      const batch = writeBatch(db);
      const gameRef = doc(collection(db, `leagues/${currentLeagueId}/games`));
      const gameData = {
        timestamp: serverTimestamp(),
        recordedBy: user.uid,
        players: results.map(r => {
          const p: any = { uid: r.uid, displayName: r.displayName, position: r.position, eloBefore: r.eloBefore, eloAfter: r.eloAfter };
          if (r.points !== undefined) p.points = r.points;
          if (r.positions !== undefined) p.positions = r.positions;
          if (r.roundEloChanges !== undefined) p.roundEloChanges = r.roundEloChanges;
          return p;
        })
      };
      batch.set(gameRef, gameData);
      batch.update(doc(db, 'leagues', currentLeagueId), { totalGames: increment(1) });
      results.forEach(r => {
        const userRef = doc(db, `leagues/${currentLeagueId}/players`, r.uid);
        batch.update(userRef, { 
          elo: r.newElo, 
          totalPoints: increment(r.points || 0), 
          gamesPlayed: increment(1), 
          lastGameAt: serverTimestamp() 
        });
      });
      await batch.commit();
      setSelectedPlayers([]);
      setPlayerPoints({});
      setRecordHighlight(false);
      setRoundOrders([]);
      setActiveRound(0);
      setView('history');
    } catch (err) {
      setError("Failed to record game. Check permissions.");
      handleFirestoreError(err, OperationType.WRITE, `leagues/${currentLeagueId}/games`);
    } finally {
      setIsRecording(false);
    }
  };

  const handleApproveJoinRequest = async (uid: string) => {
    if (!currentLeagueId) return;
    try {
      const batch = writeBatch(db);
      batch.update(doc(db, 'leagues', currentLeagueId), { pendingJoinRequests: arrayRemove(uid) });
      const globalProfileSnap = await getDoc(doc(db, 'users', uid));
      let displayName = 'Unknown', displayNameLower = 'unknown', photoURL = '';
      if (globalProfileSnap.exists()) {
        const gp = globalProfileSnap.data();
        displayName = gp.displayName || 'Unknown';
        displayNameLower = gp.displayNameLower || (gp.displayName || 'unknown').toLowerCase();
        photoURL = gp.photoURL || '';
      }
      batch.set(doc(db, `leagues/${currentLeagueId}/players`, uid), { uid, displayName, displayNameLower, photoURL, elo: 1200, totalPoints: 0, gamesPlayed: 0, leagueRole: 'member' });
      await batch.commit();
      setSuccessMessage("Join request approved.");
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setError("Failed to approve join request.");
      handleFirestoreError(err, OperationType.CREATE, `leagues/${currentLeagueId}/players/${uid}`);
    }
  };

  const handleDenyJoinRequest = async (uid: string) => {
    if (!currentLeagueId) return;
    try {
      await updateDoc(doc(db, 'leagues', currentLeagueId), { pendingJoinRequests: arrayRemove(uid) });
      setSuccessMessage("Join request denied.");
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setError("Failed to deny join request.");
      handleFirestoreError(err, OperationType.UPDATE, `leagues/${currentLeagueId}`);
    }
  };

  const toggleModerator = async (targetUser: LeaguePlayer) => {
    if (!isLeagueMod || !currentLeagueId || targetUser.leagueRole === 'admin' || targetUser.globalRole === 'admin') return;
    if (!isAdmin && !isLeagueAdmin && targetUser.leagueRole === 'moderator') {
      setError("Only league admins can remove moderator rights.");
      return;
    }
    const newRole = targetUser.leagueRole === 'moderator' ? 'member' : 'moderator';
    try {
      const userRef = doc(db, `leagues/${currentLeagueId}/players`, targetUser.uid);
      await updateDoc(userRef, { leagueRole: newRole });
      setSuccessMessage(`${targetUser.displayName} is now a ${newRole}.`);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setError("Permission denied.");
      handleFirestoreError(err, OperationType.UPDATE, `leagues/${currentLeagueId}/players/${targetUser.uid}`);
    }
  };

  const handleAddProxyProfile = async (name: string, email?: string) => {
    if (!user || !name || !isLeagueMod || !currentLeagueId) return;
    try {
      const isUnique = await checkUsernameUnique(name);
      if (!isUnique) { setError("This name is already taken by another player."); return; }
      const proxyId = `accountless_${Math.random().toString(36).substr(2, 9)}`;
      const proxyData: any = { uid: proxyId, displayName: name, displayNameLower: name.toLowerCase(), elo: 1200, totalPoints: 0, gamesPlayed: 0, isAccountless: true, createdBy: user.uid };
      if (email) proxyData.email = email.toLowerCase();
      await setDoc(doc(db, `leagues/${currentLeagueId}/players`, proxyId), proxyData);
      setSuccessMessage(`Proxy profile "${name}" created.`);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setError("Failed to add proxy profile.");
      handleFirestoreError(err, OperationType.CREATE, `leagues/${currentLeagueId}/players`);
    }
  };

  const handleDeleteUser = async (uid: string, displayName: string) => {
    if (!currentLeagueId) return;
    try {
      await deleteDoc(doc(db, `leagues/${currentLeagueId}/players`, uid));
      setSuccessMessage(`User ${displayName} removed.`);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setError("Failed to remove user. Insufficient permissions.");
      handleFirestoreError(err, OperationType.DELETE, `leagues/${currentLeagueId}/players/${uid}`);
    }
  };

  const handleUpdateProfile = async (editName: string, editPhoto: string) => {
    if (!user || !myProfile || !currentLeagueId) return;
    const isUnique = await checkUsernameUnique(editName);
    if (!isUnique) { setError("This username is already taken in this league."); return; }
    try {
      const batch = writeBatch(db);
      if (globalProfile) batch.update(doc(db, 'users', user.uid), { displayName: editName, displayNameLower: editName.toLowerCase(), photoURL: editPhoto });
      const userRef = doc(db, `leagues/${currentLeagueId}/players`, user.uid);
      batch.update(userRef, { displayName: editName, displayNameLower: editName.toLowerCase(), photoURL: editPhoto });
      await batch.commit();
      await updateProfile(user, { displayName: editName, photoURL: editPhoto });
      setSuccessMessage("Profile updated successfully!");
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setError("Failed to update profile.");
      handleFirestoreError(err, OperationType.UPDATE, `leagues/${currentLeagueId}/players/${user.uid}`);
    }
  };

  const handleUpdateLeagueName = async (newName: string) => {
    if (!currentLeagueId || !newName.trim()) return;
    try {
      await updateDoc(doc(db, 'leagues', currentLeagueId), { name: newName });
      setSuccessMessage("League name updated!");
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setError("Failed to update league name.");
      handleFirestoreError(err, OperationType.UPDATE, `leagues/${currentLeagueId}`);
    }
  };

  const handleDeleteGame = async (game: GameRecord) => {
    if (!currentLeagueId) return;
    const isMostRecent = games.length > 0 && games[0].id === game.id;
    try {
      const batch = writeBatch(db);
      batch.delete(doc(db, `leagues/${currentLeagueId}/games`, game.id));
      batch.update(doc(db, 'leagues', currentLeagueId), { totalGames: increment(-1) });
      if (isMostRecent) {
        game.players.forEach(p => {
            const userRef = doc(db, `leagues/${currentLeagueId}/players`, p.uid);
            const currentUser = users.find(u => u.uid === p.uid);
            if (currentUser) {
                const updateData: any = {
                    totalPoints: increment(-(p.points || 0)),
                    gamesPlayed: increment(-1)
                };
                // Only revert Elo if the game actually used it
                if (p.eloBefore !== undefined) {
                    updateData.elo = p.eloBefore;
                }
                batch.update(userRef, updateData);
            }
        });
      } else {
        game.players.forEach(p => {
            const userRef = doc(db, `leagues/${currentLeagueId}/players`, p.uid);
            const currentUser = users.find(u => u.uid === p.uid);
            if (currentUser) batch.update(userRef, { totalPoints: increment(-(p.points || 0)), gamesPlayed: increment(-1) });
        });
      }
      await batch.commit();
      setSuccessMessage(isMostRecent ? "Game deleted and ratings reverted." : "Old game record removed.");
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setError("Failed to delete game. Permission denied.");
      handleFirestoreError(err, OperationType.DELETE, `leagues/${currentLeagueId}/games/${game.id}`);
    }
  };

  const handleDeleteLeague = async () => {
    if (!currentLeagueId) return;
    try {
      const gameSnaps = await getDocs(collection(db, `leagues/${currentLeagueId}/games`));
      const gameDocs = gameSnaps.docs;
      for (let i = 0; i < gameDocs.length; i += 500) {
        const batch = writeBatch(db);
        gameDocs.slice(i, i + 500).forEach(d => batch.delete(d.ref));
        await batch.commit();
      }
      await deleteDoc(doc(db, 'leagues', currentLeagueId));
      const playerSnaps = await getDocs(collection(db, `leagues/${currentLeagueId}/players`));
      const playerDocs = playerSnaps.docs;
      const otherPlayers = playerDocs.filter(d => d.id !== user?.uid);
      const myPlayer = playerDocs.find(d => d.id === user?.uid);
      for (let i = 0; i < otherPlayers.length; i += 500) {
        const batch = writeBatch(db);
        otherPlayers.slice(i, i + 500).forEach(d => batch.delete(d.ref));
        await batch.commit();
      }
      if (myPlayer) await deleteDoc(myPlayer.ref);
      setSuccessMessage("League deleted successfully.");
      setCurrentLeagueId(null);
      setView('leaderboard');
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setError("Failed to delete league. Insufficient permissions.");
      handleFirestoreError(err, OperationType.DELETE, `leagues/${currentLeagueId}`);
    }
  };

  const handleApprovePendingGame = async (game: GameRecord) => {
    if (!currentLeagueId || !isLeagueMod) return;
    try {
      const batch = writeBatch(db);
      const gameRef = doc(collection(db, `leagues/${currentLeagueId}/games`));
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id, ...gameData } = game;
      batch.set(gameRef, { 
        ...gameData, 
        timestamp: serverTimestamp(), // Update timestamp to when it was approved
        approvedAt: serverTimestamp(), 
        approvedBy: user?.uid 
      });
      batch.update(doc(db, 'leagues', currentLeagueId), { totalGames: increment(1) });

      for (const p of game.players) {
        const playerRef = doc(db, `leagues/${currentLeagueId}/players`, p.uid);
        const playerSnap = await getDoc(playerRef);
        if (playerSnap.exists()) {
          batch.update(playerRef, {
            elo: p.eloAfter !== undefined ? p.eloAfter : (playerSnap.data()?.elo || 1200),
            totalPoints: increment(p.points || 0),
            gamesPlayed: increment(1),
            lastGameAt: serverTimestamp()
          });
        } else {
          // Document does not exist in league players yet!
          let displayName = p.displayName || 'Unknown';
          let displayNameLower = (p.displayName || 'unknown').toLowerCase();
          let photoURL = '';
          let isAccountless = true;

          try {
            const globalUserSnap = await getDoc(doc(db, 'users', p.uid));
            if (globalUserSnap.exists()) {
              const gu = globalUserSnap.data();
              displayName = gu.displayName || displayName;
              displayNameLower = (gu.displayNameLower || gu.displayName || displayName).toLowerCase();
              photoURL = gu.photoURL || '';
              isAccountless = false;
            }
          } catch (e) {
            console.warn("Could not check global user:", e);
          }

          const newPlayerData: any = {
            uid: p.uid,
            displayName,
            displayNameLower,
            photoURL,
            elo: p.eloAfter !== undefined ? p.eloAfter : (p.eloBefore !== undefined ? p.eloBefore : 1200),
            totalPoints: p.points || 0,
            gamesPlayed: 1,
            leagueRole: 'member',
            createdAt: serverTimestamp(),
            lastGameAt: serverTimestamp()
          };

          if (isAccountless) {
            newPlayerData.isAccountless = true;
            newPlayerData.createdBy = game.recordedBy || user?.uid || '';
          }

          batch.set(playerRef, newPlayerData);
        }
      }

      batch.delete(doc(db, `leagues/${currentLeagueId}/pending_games`, game.id));
      await batch.commit();
      setSuccessMessage("Game approved and added to history.");
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setError("Failed to approve game.");
      handleFirestoreError(err, OperationType.UPDATE, `leagues/${currentLeagueId}/pending_games/${game.id}`);
    }
  };

  const handleRejectPendingGame = async (gameId: string) => {
    if (!currentLeagueId || !isLeagueMod) return;
    try {
      await deleteDoc(doc(db, `leagues/${currentLeagueId}/pending_games`, gameId));
      setSuccessMessage("Pending game rejected.");
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setError("Failed to reject game.");
      handleFirestoreError(err, OperationType.DELETE, `leagues/${currentLeagueId}/pending_games/${gameId}`);
    }
  };

  const contextValue = React.useMemo(() => ({
    currentLeagueId, setCurrentLeagueId, users, games, pendingGames, handleApprovePendingGame, handleRejectPendingGame, view, setView, error, setError, successMessage, setSuccessMessage, selectedPlayers, setSelectedPlayers, playerPoints, setPlayerPoints, roundOrders, setRoundOrders, activeRound, setActiveRound, isRecording, handleRecordGame, handleNextRound, claimingProfileId, setClaimingProfileId, handleRequestTakeover, handleCancelTakeover, handleApproveTakeover, handleDismissTakeover, checkUsernameUnique, isAdmin, isLeagueMod, isLeagueAdmin, myProfile, handleApproveJoinRequest, handleDenyJoinRequest, toggleModerator, handleAddProxyProfile, handleDeleteUser, handleUpdateProfile, handleUpdateLeagueName, handleDeleteGame, handleDeleteLeague, handleJoinLeague, handleRepairLeagueStats, joinStatus, isPlayersLoading, recordHighlight, setRecordHighlight, leagueData, myStats, gameSpecifier
  }), [
    currentLeagueId, setCurrentLeagueId, users, games, pendingGames, view, setView, error, setError, successMessage, setSuccessMessage, selectedPlayers, setSelectedPlayers, playerPoints, setPlayerPoints, roundOrders, setRoundOrders, activeRound, setActiveRound, isRecording, handleRecordGame, handleNextRound, claimingProfileId, setClaimingProfileId, handleRequestTakeover, handleCancelTakeover, handleApproveTakeover, handleDismissTakeover, checkUsernameUnique, isAdmin, isLeagueMod, isLeagueAdmin, myProfile, handleApproveJoinRequest, handleDenyJoinRequest, toggleModerator, handleAddProxyProfile, handleDeleteUser, handleUpdateProfile, handleUpdateLeagueName, handleDeleteGame, handleDeleteLeague, handleJoinLeague, handleRepairLeagueStats, joinStatus, isPlayersLoading, recordHighlight, setRecordHighlight, leagueData, myStats, gameSpecifier
  ]);

  return (
    <LeagueContext.Provider value={contextValue}>
      {children}
    </LeagueContext.Provider>
  );
}

export function useLeague() {
  const context = useContext(LeagueContext);
  return context;
}
